import { Autocomplete, CircularProgress, FormControl, SvgIcon, TextField } from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { SearchSM } from 'untitledui-js-base';
import { SeznamAutocomplete } from '../../types/external/seznamAutocomplete';

interface AutocompleteAddressProps {
  autocompleteItems: SeznamAutocomplete[];
  onSearch?: (value: string) => void;
  onSelect?: (value: SeznamAutocomplete) => void;
  isLoading?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  formatSelected?: (item: SeznamAutocomplete) => string;
  clearOnClose?: boolean;
  freeSolo?: true;
}

export const AutocompleteInput = ({
  autocompleteItems,
  onSearch,
  onSelect,
  isLoading,
  value,
  onValueChange,
  defaultValue = '',
  formatSelected,
  clearOnClose,
  freeSolo = true
}: AutocompleteAddressProps) => {
  const isControlled = value !== undefined;

  const [inputValue, setInputValue] = useState<string>(defaultValue ?? '');
  useEffect(() => {
    if (!isControlled) setInputValue(defaultValue ?? '');
  }, [defaultValue, isControlled]);

  const [isFocused, setIsFocused] = useState(false);
  const open = isFocused && !isEmpty(autocompleteItems);

  const currentValue = isControlled ? value ?? '' : inputValue;
  const setCurrentValue = (v: string) => {
    if (isControlled) onValueChange?.(v);
    else setInputValue(v);
  };

  const toLabel = useMemo(
    () =>
      formatSelected ??
      ((item: SeznamAutocomplete) => [item?.data?.name, item?.data?.location].filter(Boolean).join(' ')),
    [formatSelected]
  );

  const handleClose = (selected?: SeznamAutocomplete | string | null) => {
    setIsFocused(false);
    if (selected == null) {
      if (clearOnClose) setCurrentValue('');
      return;
    }
    if (typeof selected === 'string') {
      setCurrentValue(selected);
      return;
    }
    onSelect?.(selected);
    setCurrentValue(toLabel(selected));
  };

  return (
    <FormControl fullWidth>
      <Autocomplete<SeznamAutocomplete, false, false, true>
        open={open}
        filterOptions={(x) => x}
        options={autocompleteItems ?? []}
        loading={isLoading}
        freeSolo={freeSolo}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name ?? '')}
        isOptionEqualToValue={(option, v) => (typeof v === 'string' ? option.name === v : option.id === v.id)}
        onChange={(_, v) => handleClose(v)}
        onClose={() => handleClose(null)}
        inputValue={currentValue}
        onInputChange={(_, v, reason) => {
          setCurrentValue(v ?? '');
          if (reason === 'input') onSearch?.(v ?? '');
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            label="Vyhledávejte adresu"
            placeholder="Začněte vyhledávat..."
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <SvgIcon color="inherit" fontSize="inherit">
                    <SearchSM />
                  </SvgIcon>
                ),
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
      />
    </FormControl>
  );
};
